import { apolloProvider } from '@/apollo-provider.js'
import {
  MUTATION_UPSERT_CAMPAIGN_VARIABLES_CONFIGURATION,
  QUERY_GET_CAMPAIGN_VARIABLES_CONFIGURATION,
  QUERY_GET_BALANCE_BY_CHANNEL,
  QUERY_GET_OMNI_CAMPAIGN,
  QUERY_GET_DAYS_ON_OFFERS,
} from '@/modules/maria/campanha-da-maria-v2/graphql'
import { QUERY_SALES_CAMPAIGN_BY_ID } from '@/modules/campanhas/gestao-ofertas-v2/graphql'
import { microservicesUrls } from '@/plugins/microservices/microservices'
import { deepDelete } from '@/helpers/deepDelete'

export default {
  async getOmniCampaign({ commit }, variables) {
    try {
      const result = await apolloProvider.defaultClient.query({
        query: QUERY_GET_OMNI_CAMPAIGN,
        variables,
        fetchPolicy: 'no-cache',
      })
      const omniCampaign = result.data.omniCampaign

      if (omniCampaign.offer_schedules && Array.isArray(omniCampaign.offer_schedules)) {
        const offerSchedulesWithModels = await Promise.all(
          omniCampaign.offer_schedules.map(async (schedule) => {
            const offerScheduleId = schedule?.offer_schedule?._id
            if (!offerScheduleId) {
              return schedule
            }

            try {
              const scheduleResult = await apolloProvider.defaultClient.query({
                query: QUERY_SALES_CAMPAIGN_BY_ID,
                variables: {
                  id: offerScheduleId,
                  franchise: false,
                },
                context: {
                  uri: microservicesUrls['sales'],
                },
                fetchPolicy: 'no-cache',
              })

              const offerScheduleData = scheduleResult.data.offerSchedule

              const mergeProductsWithEligibleClients = (scheduleProducts, fullScheduleProducts, offerType) => {
                if (!fullScheduleProducts || !Array.isArray(fullScheduleProducts) || !scheduleProducts || !Array.isArray(scheduleProducts)) {
                  return scheduleProducts
                }

                return scheduleProducts.map((scheduleProduct) => {
                  if (scheduleProduct.eligible_clients) {
                    if (scheduleProduct.offer_sales && Array.isArray(scheduleProduct.offer_sales)) {
                      const fullGroup = fullScheduleProducts.find(
                        (g) =>
                          g._id === scheduleProduct._id ||
                          g.group_id === scheduleProduct.group_id ||
                          (g.groupInfo && g.groupInfo._id === scheduleProduct.groupInfo?._id)
                      )
                      if (fullGroup?.offer_sales) {
                        scheduleProduct.offer_sales = mergeProductsWithEligibleClients(scheduleProduct.offer_sales, fullGroup.offer_sales, offerType)
                      }
                    }
                    return scheduleProduct
                  }

                  const isGroup = scheduleProduct.offer_sales && Array.isArray(scheduleProduct.offer_sales)
                  const hasGroupId = scheduleProduct.group_id || scheduleProduct.groupInfo?._id

                  if (isGroup || hasGroupId) {
                    const groupId = scheduleProduct._id || scheduleProduct.group_id || scheduleProduct.groupInfo?._id
                    const fullGroup = fullScheduleProducts.find(
                      (g) => g._id === groupId || g.group_id === groupId || (g.groupInfo && g.groupInfo._id === groupId)
                    )

                    if (fullGroup) {
                      if (fullGroup.eligible_clients) {
                        scheduleProduct.eligible_clients = fullGroup.eligible_clients
                      }

                      if (scheduleProduct.offer_sales && fullGroup.offer_sales) {
                        scheduleProduct.offer_sales = mergeProductsWithEligibleClients(scheduleProduct.offer_sales, fullGroup.offer_sales, offerType)
                      }

                      return scheduleProduct
                    }
                  }

                  let fullProduct = fullScheduleProducts.find((p) => p.sku === scheduleProduct.sku && !p.offer_sales)

                  if (!fullProduct) {
                    for (const fullItem of fullScheduleProducts) {
                      if (fullItem.offer_sales && Array.isArray(fullItem.offer_sales)) {
                        const productInGroup = fullItem.offer_sales.find((p) => p.sku === scheduleProduct.sku)
                        if (productInGroup?.eligible_clients) {
                          return { ...scheduleProduct, eligible_clients: productInGroup.eligible_clients }
                        }
                      }
                      if (fullItem[offerType] && Array.isArray(fullItem[offerType])) {
                        const productInGroup = fullItem[offerType].find((p) => p.sku === scheduleProduct.sku)
                        if (productInGroup?.eligible_clients) {
                          return { ...scheduleProduct, eligible_clients: productInGroup.eligible_clients }
                        }
                      }
                      if (fullItem.sku === scheduleProduct.sku && fullItem.eligible_clients && !fullItem.offer_sales) {
                        fullProduct = fullItem
                        break
                      }
                    }
                  }

                  if (fullProduct?.eligible_clients) {
                    return { ...scheduleProduct, eligible_clients: fullProduct.eligible_clients }
                  }

                  return scheduleProduct
                })
              }

              const mergedSchedule = {
                ...schedule,
                auto_distributed: offerScheduleData?.auto_distributed ?? schedule?.auto_distributed ?? false,
                max_best_offer_model_clients:
                  offerScheduleData?.max_best_offer_model_clients ??
                  schedule?.max_best_offer_model_clients ??
                  schedule?.offer_schedule?.max_best_offer_model_clients ??
                  {},
                user_distribution_models: offerScheduleData?.user_distribution_models ?? schedule?.user_distribution_models ?? [],
                offer_schedule: {
                  ...schedule.offer_schedule,
                  auto_distributed: offerScheduleData?.auto_distributed ?? schedule?.offer_schedule?.auto_distributed ?? false,
                  max_best_offer_model_clients:
                    offerScheduleData?.max_best_offer_model_clients ??
                    schedule?.offer_schedule?.max_best_offer_model_clients ??
                    schedule?.max_best_offer_model_clients ??
                    {},
                  user_distribution_models: offerScheduleData?.user_distribution_models ?? schedule?.offer_schedule?.user_distribution_models ?? [],
                },
              }

              const offerTypes = ['offer_sales', 'percentage_offer_sales', 'takepay_offer_sales', 'combo_offer_sales']
              offerTypes.forEach((offerType) => {
                if (schedule[offerType] && offerScheduleData[offerType]) {
                  mergedSchedule[offerType] = mergeProductsWithEligibleClients(schedule[offerType], offerScheduleData[offerType], offerType)
                }
              })

              return mergedSchedule
            } catch (error) {
              console.error(`Failed to get offer_schedule data (including max_best_offer_model_clients) for offer_schedule ${offerScheduleId}`, error)
              return schedule
            }
          })
        )

        omniCampaign.offer_schedules = offerSchedulesWithModels
      }

      commit('SET_OMNI_CAMPAIGN', omniCampaign)
      commit('SET_PREVIOUS_OFFER_SCHEDULES', omniCampaign.offer_schedules || [])
    } catch (error) {
      console.error('Failed to get omni campaign', error)
    }
  },
  setOmniCampaignKey({ commit }, payloadObject) {
    try {
      commit('SET_OMNI_CAMPAIGN_KEY', payloadObject)
    } catch (error) {
      console.error('Failed to set omni campaign key', error)
    }
  },
  setOmniCampaignLimiter({ commit }, value) {
    try {
      commit('SET_OMNI_CAMPAIGN_LIMITER', value)
    } catch (error) {
      console.error('Failed to set omni campaign limiter', error)
    }
  },
  setOmniCampaignBudget({ commit }, payloadObject) {
    try {
      commit('SET_OMNI_CAMPAIGN_BUDGET', payloadObject)
    } catch (error) {
      console.error('Failed to set omni campaign budget', error)
    }
  },
  setProductsSearchValue({ commit }, payload) {
    try {
      commit('SET_SEARCH_PRODUCTS_VALUE', payload)
    } catch (error) {
      console.error('Failed to set search value', error)
    }
  },
  setLoadingAction({ commit }, value) {
    commit('SET_LOADING_ACTION', value)
  },
  setMessagesDispatcherSettingsChannel({ commit }, payload) {
    try {
      commit('SET_MESSAGES_DISPATCHER_SETTINGS_CHANNEL', payload)
    } catch (error) {
      throw new Error('Failed to set messages dispatcher settings channel', error)
    }
  },
  setVariableCharCount({ commit }, { variable, charCount }) {
    try {
      commit('SET_VARIABLE_CHAR_COUNT', { variable, charCount })
    } catch (error) {
      throw new Error('Failed to set variables char count', error)
    }
  },
  setAllChannelsCharCount({ commit }, payload) {
    try {
      commit('SET_ALL_CHANNELS_CHAR_COUNT', payload)
    } catch (error) {
      throw new Error('Failed to set all channels char count', error)
    }
  },
  async getDaysOnOffers({ commit }, variables) {
    try {
      const result = await apolloProvider.defaultClient.query({
        query: QUERY_GET_DAYS_ON_OFFERS,
        variables: {
          input: variables,
        },
        fetchPolicy: 'no-cache',
      })
      const daysOnOffers = result.data.daysOnOffers
      commit('SET_DAYS_ON_OFFERS', daysOnOffers)
    } catch (error) {
      console.error('Failed to get days on offers', error)
    }
  },
  async getAvailableBalance() {
    try {
      const { data } = await apolloProvider.defaultClient.query({
        query: QUERY_GET_BALANCE_BY_CHANNEL,
        fetchPolicy: 'no-cache',
      })
      return data
    } catch (error) {
      console.error('Failed to get available balance', error)
    }
  },
  async upsertCampaignVariablesConfiguration({ commit }, variables) {
    try {
      const result = await apolloProvider.defaultClient.mutate({
        mutation: MUTATION_UPSERT_CAMPAIGN_VARIABLES_CONFIGURATION,
        fetchPolicy: 'no-cache',
        variables: {
          data: variables,
        },
      })
      const { _id, message, status } = result.data.upsertCampaignVariablesConfiguration
      if (status !== 'SUCCESS') {
        throw new Error(`Failed to upsert campaign variables configuration: ${message}`)
      }
      commit('SET_ID_CAMPAIGN_VARIABLES_CONFIGURATION', _id)
    } catch (error) {
      throw new Error('Failed to upsert campaign variables configuration', error)
    }
  },
  async getCampaignVariablesConfiguration({ commit }, variables) {
    try {
      const { campaignVariablesConfiguration } = await apolloProvider.defaultClient
        .query({
          query: QUERY_GET_CAMPAIGN_VARIABLES_CONFIGURATION,
          variables,
          fetchPolicy: 'no-cache',
        })
        .then(({ data: { campaignVariablesConfiguration } }) => {
          return { campaignVariablesConfiguration }
        })

      deepDelete(campaignVariablesConfiguration, '__typename')
      commit('SET_CAMPAIGN_VARIABLES_CONFIGURATION', campaignVariablesConfiguration)
    } catch (error) {
      console.error('Failed to get campaign variables configuration', error)
    }
  },
  setPreviousOfferSchedules({ commit }, offerSchedules) {
    commit('SET_PREVIOUS_OFFER_SCHEDULES', offerSchedules)
  },
}
